"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, Search, FileText, Globe as Globe2, Layers, Link2, Smartphone, Mic, Target, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
const services = [{
  id: 1,
  title: "Google Business Profile Optimization",
  category: "Local Visibility",
  description: "Complete setup, verification, and technical optimization of your Google Business Profile to capture high-intent local map pack searches.",
  deliverable: "Map Pack Top 3 Focus",
  iconName: "MapPin",
  href: "/LocalSEOPage"
}, {
  id: 2,
  title: "Local Citation Building & Audit",
  category: "NAP Consistency",
  description: "Auditing and claiming authoritative business directory listings across key UK and global platforms to reinforce geographic authority.",
  deliverable: "100% Consistent Data",
  iconName: "Building2",
  href: "/LocalSEOPage"
}, {
  id: 3,
  title: "Review & Reputation Strategy",
  category: "Trust & Conversion",
  description: "Ethical strategies to generate customer reviews, optimize review response workflows, and signal high trust to Google search algorithms.",
  deliverable: "Higher Conversion Rates",
  iconName: "Star",
  href: "/LocalSEOPage"
}, {
  id: 4,
  title: "Local Schema Markup & Structured Data",
  category: "Technical SEO",
  description: "Implementing JSON-LD LocalBusiness schema, geo-coordinates, opening hours, and service schemas so search engines fully parse your business data.",
  deliverable: "Rich Snippet Eligibility",
  iconName: "Code2",
  href: "/TechnicalSeoPage"
}, {
  id: 5,
  title: "Local Keyword Research & Intent Mapping",
  category: "Strategy",
  description: "In-depth identification of commercial and transactional search terms tailored to specific target towns, cities, and regional catchment areas.",
  deliverable: "Targeted Search Mapping",
  iconName: "Search",
  href: "/LocalSEOPage"
}, {
  id: 6,
  title: "On-Page Local SEO Optimization",
  category: "On-Page SEO",
  description: "Refining title tags, meta descriptions, heading structures, and internal links with localized relevance and clear commercial search intent.",
  deliverable: "Fully Optimized Pages",
  iconName: "FileText",
  href: "/LocalSEOPage"
}, {
  id: 7,
  title: "Content Localization & Geo-Copywriting",
  category: "Content Strategy",
  description: "Creating authentic, engaging local landing content that highlights local expertise, testimonials, and regional relevance without doorway spam.",
  deliverable: "Engaging Local Content",
  iconName: "Globe2",
  href: "/LocalSEOPage"
}, {
  id: 8,
  title: "Multi-Location SEO Architecture",
  category: "Enterprise Local",
  description: "Scalable site architecture designed for multi-branch companies and franchise networks targeting distinct regional search markets.",
  deliverable: "Scalable Site Hierarchy",
  iconName: "Layers",
  href: "/LocalSEOPage"
}, {
  id: 9,
  title: "Local Link Building & Regional Outreach",
  category: "Authority Building",
  description: "Earning genuine hyper-local backlinks from regional news, industry partners, sponsorships, and niche directories to boost domain trust.",
  deliverable: "High-Authority Backlinks",
  iconName: "Link2",
  href: "/LocalSEOPage"
}, {
  id: 10,
  title: "Mobile Search Optimization",
  category: "User Experience",
  description: "Ensuring lightning-fast page speed, seamless click-to-call functionality, and effortless mobile navigation for local searchers on the move.",
  deliverable: "Mobile-First Excellence",
  iconName: "Smartphone",
  href: "/TechnicalSeoPage"
}, {
  id: 11,
  title: "Voice Search & AI Local Optimization",
  category: "Next-Gen SEO",
  description: "Structuring conversational Q&A content and entity relationships so your business appears in AI overviews and voice query responses.",
  deliverable: "AI Overview Ready",
  iconName: "Mic",
  href: "/AISearchOptimisationPage"
}, {
  id: 12,
  title: "Geo-Targeted Landing Pages",
  category: "Lead Generation",
  description: "Designing hyper-focused, high-converting location pages tailored to convert local search traffic into direct qualified business inquiries.",
  deliverable: "High-Converting Pages",
  iconName: "Target",
  href: "/B2BSeoLeadGenerationPage"
}];
export default function Consultancyservicesgrid() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const renderIcon = (iconName: string) => {
    const iconProps = {
      className: "w-5 h-5 text-primary-foreground"
    };
    switch (iconName) {
      case "MapPin":
        return <MapPin {...iconProps} />;
      case "Building2":
        return <Building2 {...iconProps} />;
      case "Star":
        return <Star {...iconProps} />;
      case "Code2":
        return <Code2 {...iconProps} />;
      case "Search":
        return <Search {...iconProps} />;
      case "FileText":
        return <FileText {...iconProps} />;
      case "Globe2":
        return <Globe2 {...iconProps} />;
      case "Layers":
        return <Layers {...iconProps} />;
      case "Link2":
        return <Link2 {...iconProps} />;
      case "Smartphone":
        return <Smartphone {...iconProps} />;
      case "Mic":
        return <Mic {...iconProps} />;
      case "Target":
        return <Target {...iconProps} />;
      default:
        return <MapPin {...iconProps} />;
    }
  };
  return <section id="consultancyservicesgrid" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary bg-primary/5">
            <Sparkles className="w-3.5 h-3.5 mr-1.5 text-primary" />
            Direct Consultant Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground mb-4">
            Specialist Local & National SEO Services
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            I work directly with UK and international businesses to build sustainable search visibility, capture local commercial intent, and generate qualified leads.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => <motion.div key={service.id} data-index={i} variants={itemVariants} className="h-full">
              <Card className="bg-card border border-border/80 shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-300 rounded-xl overflow-hidden flex flex-col justify-between h-full group">
                <div>
                  <div className="bg-primary text-primary-foreground p-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-primary-foreground/10 text-primary-foreground border border-primary-foreground/20 shrink-0">
                        {renderIcon(service.iconName)}
                      </div>
                      <span className="text-xs font-medium uppercase tracking-wider text-primary-foreground/90 bg-primary-foreground/15 px-2.5 py-1 rounded-full">
                        {service.category}
                      </span>
                    </div>
                  </div>

                  <CardHeader className="pt-6 pb-2 px-6">
                    <CardTitle className="text-lg font-bold font-serif text-card-foreground group-hover:text-primary transition-colors duration-200">
                      {service.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 pb-4">
                    <p className="text-muted-foreground text-sm leading-relaxed mb-5">
                      {service.description}
                    </p>
                    <div className="flex items-center gap-2 text-xs font-medium text-foreground bg-muted/60 p-2.5 rounded-md border border-border/50">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      <span>Deliverable: {service.deliverable}</span>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="px-6 pb-6 pt-2">
                  <Button variant="outline" size="sm" asChild className="w-full justify-between hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                    <Link to={service.href}>
                      <span>Explore Service</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 sm:mt-20 rounded-2xl bg-muted/50 border border-border p-8 lg:p-12 text-center max-w-4xl mx-auto shadow-xs">
          <h3 className="text-2xl lg:text-3xl font-bold font-serif text-foreground mb-3">
            Ready to Drive Organic Search Growth?
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6 text-sm sm:text-base leading-relaxed">
            When you consult with YE Consultancy, you work directly with me — Yassine Essaadi. No account managers, no junior handoffs. Just tailored SEO strategy and transparent execution.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Button size="lg" asChild className="w-full sm:w-auto">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="w-full sm:w-auto">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}